/**
 * Alert <uo-accordion-item>
 *
 * Attributes:
 */

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property, queryAssignedElements } from 'lit/decorators.js';
import { isDefined } from '@automator/shared/utils/is-defined';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import style for web component
import styles from './web-component-style.scss?inline';

@customElement( 'uo-accordion-item' ) // eslint-disable-line require-jsdoc
export class AutomatorAccordionItem extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	@property( { type: Boolean } ) accessor open = false;

	@property( { type: Boolean } ) accessor disabled = false;

	// References
	@queryAssignedElements( { selector: 'uo-accordion-item-li' } ) accessor $items;

	@queryAssignedElements( { slot: 'summary' } ) accessor $summary;

	// eslint-disable-next-line require-jsdoc
	constructor() {
		super();
	}

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div class="item"
				?open=${ this.open }
				?disabled=${ this.disabled }
			>

				<div
					class="summary"
					@click=${ this._toggleItem }
				>
					<div class="summary-content">
						<slot name="summary"></slot>
					</div>

					<div class="summary-toggle">
						<uo-icon id="angle-down"></uo-icon>
					</div>
				</div>

				<div class="content">
					<slot></slot>
				</div>

			</div>
		`;
	}

	/**
	 * Toggle the item
	 */
	async _toggleItem() {
		// Set the inverse value
		this.open = ! this.open;

		// Wait for `open` state to be rendered to the DOM
		await this.updateComplete;

		// Dispatch de toggle event
		this.dispatchEvent(
			new CustomEvent( 'toggle', {
				bubbles: true,
				composed: true
			} )
		);
	}

	/**
	 * Returns an array with information about the items
	 * We will use this to perform searches
	 *
	 * @return {Array} Array with data about each item
	 */
	get _searchMap() {
		// Check if there isn't a search map defined
		if ( ! isDefined( this._internalSearchMap ) ) {
			// Create it
			this.updateSearchMap();
		}

		// Return it
		return this._internalSearchMap;
	}

	/**
	 * Sets the search map
	 *
	 * @param  {Array} data The search map data
	 * @return {undefined}
	 */
	set _searchMap( data = [] ) {
		this._internalSearchMap = data;
	}

	/**
	 * Creates and saves a search map with data about each item
	 */
	updateSearchMap() {
		const searchMap = [];

		// Check if it has items
		if ( ! isEmpty( this.$items ) ) {

			// Define the default shared keywords
			// We're going to use these to search
			const sharedKeywords = [];

			// Add the summary text as a keyword
			if ( isDefined( this.$summary[0] ) ) {
				sharedKeywords.push( this.$summary[0].innerText );
			}

			// Iterate the items
			this.$items.forEach( ( $item ) => {

				const itemData = {
					keywords: sharedKeywords
				};

				// Add accordion
				itemData.$accordion = this;

				// Create reference to the DOM element
				itemData.$item = $item;

				// Get the title
				if (
					isDefined( $item.search )
					&& isDefined( $item.search.title )
				) {
					itemData.title = $item.search.title;
				} else {
					itemData.title = $item.innerText;
				}

				// Get the keywords
				if (
					isDefined( $item.search )
					&& isDefined( $item.search.keywords )
				) {
					itemData.keywords = Object.assign( itemData.keywords, $item.search.keywords );
				}

				// Add item
				searchMap.push( itemData );
			} );

		}

		// Save the values
		this._searchMap = searchMap;
	}
}
